import React, { useEffect, useState } from 'react'

import { useHistory } from 'react-router-dom'

import * as UserEvents from 'redux/api/internal/UserEvents'

import { Action } from 'types/GlobalUserEvents'
import { TReport, TReportEdition } from 'types/GlobalReport'
import { TCommentThreadData } from 'types/GlobalComment'
import { CustomPeriodFilter, PeriodFilter } from 'types/GlobalWidget'
import { EPublicationStatus } from 'types/Enums'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useLocation } from 'react-router-dom'
import { useIsMobile, useParams, useQuery } from 'utils/hooks'
import { useReport, useReportEdition } from 'redux/hooks/Reports'
import { useCommentThreadsForReportEdition } from 'redux/hooks/Comments'
import { TFunction, useTranslation } from 'language/translator'

import { useReportEditionActions } from 'redux/actions/ReportEditions'
import { useWidgetActions } from 'redux/actions/Widgets'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import { BreadcrumbsProps } from 'components_new/molecules/Breadcrumbs'
import DisabledWrapper from 'components_new/molecules/DisabledWrapper'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'

import ReportBlock from 'components_new/organisms/ReportBlock'
import ReportEditionSettings from 'components_new/organisms/ReportEditionSettings'
import ReportEditionSidebar from 'components_new/organisms/ReportEditionSidebar'
import ReportHeader from 'components_new/organisms/ReportHeader'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'

import PageLayout from 'components_new/templates/PageLayout'

import { getContext } from 'utils/userEvents'

import { getParsedDate } from 'utils/dateParser'

const getBreadcrumbs = (
  report: TReport,
  edition: TReportEdition,
  t: TFunction
): BreadcrumbsProps['items'] => {
  return [
    {
      href: '/resources',
      icon: { name: 'Resources' },
      title: t('global', 'resources')
    },
    {
      href: `/reports/${report.id}`,
      icon: { name: 'Reports' as const },
      title: report.title
    },
    {
      href: `/reports/${report.id}/editions/${edition.id}`,
      title: edition.title ?? ''
    }
  ]
}

const ReportEdition = () => {
  const { edition_id, id } = useParams<{ edition_id: string; id: string }>()
  const history = useHistory()

  const edition = useReportEdition(edition_id)
  const { isAdmin } = useAccountSettings()
  const isMobile = useIsMobile()
  const report = useReport(id)

  const { tryDeleteReportEdition, tryToggleReportEditionStatus } =
    useReportEditionActions()
  const { invalidateWidgets } = useWidgetActions()

  const { t, lang } = useTranslation()

  const { query } = useQuery()
  const [editMode, setEditMode] = useState<boolean>(Boolean(query['edit']))
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>(
    {}
  )
  const [periodFilter, setPeriodFilter] = useState<
    PeriodFilter | CustomPeriodFilter
  >(PeriodFilter.LAST_12_MONTHS_TO_TODAY)

  const [deleteOpen, setDeleteOpen] = useState<boolean>(false)
  const [tab, setTab] = useState<'tasks' | 'comment-threads'>('tasks')
  const [newCommentThread, setNewCommentThread] =
    useState<TCommentThreadData | null>(null)
  const [rightSidebarCollapsed, setRightSidebarCollapsed] =
    useState<boolean>(isMobile)
  const [focusedCommentThreadId, setFocusedCommentThreadId] = useState<
    string | null
  >(null)
  const [activeWidgetId, setActiveWidgetId] = useState<string | null>(null)

  const commentThreads = useCommentThreadsForReportEdition(edition_id)

  // open comments
  const needsDelay = rightSidebarCollapsed || tab !== 'comment-threads'

  // add comment
  const handleAddComment = (widgetId: string, data: TCommentThreadData) => {
    const existing = commentThreads.find(
      (thread) =>
        thread.widgetId === widgetId &&
        thread.data.label === data.label &&
        thread.data.datasetLabel === data.datasetLabel
    )

    if (existing) {
      setFocusedCommentThreadId(existing.id)

      return
    }

    setRightSidebarCollapsed(false)
    setTab('comment-threads')

    if (needsDelay) {
      setTimeout(() => {
        setActiveWidgetId(widgetId)
        setFocusedCommentThreadId(null)
        setNewCommentThread(data)
      }, 300)
    } else {
      setActiveWidgetId(widgetId)
      setFocusedCommentThreadId(null)
      setNewCommentThread(data)
    }
  }

  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)

  const handleSetActiveFilters = (
    newFilterValue: React.SetStateAction<Record<string, string[]>>
  ) => {
    invalidateWidgets()
    setActiveFilters(newFilterValue)
  }

  const handleSetPeriodFilter = (
    newPeriod: React.SetStateAction<PeriodFilter | CustomPeriodFilter>
  ) => {
    invalidateWidgets()
    setPeriodFilter(newPeriod)
  }

  useEffect(() => {
    if (id) {
      const context = getContext(searchParams)

      UserEvents.log({
        action: Action.REPORT_OPENED,
        context: context,
        report_id: id
      })
    }
  }, [id])

  // loading
  if (!edition || !report) return <></>

  const breadcrumbs: BreadcrumbsProps['items'] = getBreadcrumbs(
    report,
    edition,
    t
  )

  // success
  return (
    <>
      <PageLayout
        content={{ editMode: { active: editMode } }}
        rightSidebar={{
          collapsed: rightSidebarCollapsed,
          setCollapsed: setRightSidebarCollapsed,
          content: (
            <ReportEditionSidebar
              focusedCommentThreadId={focusedCommentThreadId}
              id={edition_id}
              newCommentThread={newCommentThread}
              setFocusedCommentThreadId={setFocusedCommentThreadId}
              setNewCommentThread={setNewCommentThread}
              setTab={setTab}
              tab={tab}
              widgetId={activeWidgetId}
            />
          ),
          size: 'medium'
        }}
        topbar={{
          actions: isAdmin ? (
            <>
              <Button
                onClick={() => setEditMode(!editMode)}
                variant={editMode ? 'contained' : 'text'}
              >
                {t('global', editMode ? 'done' : 'edit')}
              </Button>
            </>
          ) : null,
          breadcrumbs,
          options: [
            {
              iconName:
                edition.status === EPublicationStatus.PUBLISHED
                  ? 'Unpublished'
                  : 'Published',
              onClick: () =>
                tryToggleReportEditionStatus(edition_id, edition.status),
              title:
                edition.status === EPublicationStatus.PUBLISHED
                  ? t('global', 'unpublish')
                  : t('global', 'publish')
            },
            {
              iconName: 'Delete',
              onClick: () => setDeleteOpen(true),
              title: t('global', 'delete')
            }
          ]
        }}
        variant="feed"
      >
        <ReportHeader
          disabled={editMode}
          editMode={false}
          id={id}
          sx={{ mb: 2 }}
        />

        <ReportEditionSettings
          activeFilters={activeFilters}
          editMode={editMode}
          periodFilter={periodFilter}
          setActiveFilters={handleSetActiveFilters}
          setEditMode={setEditMode}
          setPeriodFilter={handleSetPeriodFilter}
          sx={{ mb: 4 }}
        />

        {edition.blocks.length === 0 ? (
          <EmptyPlaceholder
            actions={
              <Button
                endIcon={<Icon name="ArrowForward" />}
                href={`/reports/${id}`}
              >
                {t('reports', 'go_to_template')}
              </Button>
            }
            background={true}
            body={t('reports', 'empty_widgets_body')}
            height={172}
            size="small"
            sx={{ mb: 4 }}
            title={t('reports', 'empty_widgets_title')}
          />
        ) : (
          <DisabledWrapper disabled={editMode && edition.locked_at === null}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 4, mb: 4 }}
            >
              {edition.blocks.map((block) => {
                return (
                  <ReportBlock
                    activeFilters={activeFilters}
                    block={block}
                    editMode={editMode}
                    key={block.id}
                    locked={edition.locked_at !== null}
                    onAddComment={handleAddComment}
                    periodFilter={periodFilter}
                    variant="edition"
                  />
                )
              })}
            </Box>
          </DisabledWrapper>
        )}

        <Divider sx={{ mb: 4 }} />

        <Text align="center" color="text.secondary" variant="caption">
          {report.title} · {edition.title}
          {edition.locked_at ? (
            <>
              {' · '}
              {t('report_edition', 'data_locked')}{' '}
              {getParsedDate(edition.locked_at, lang, { time: 'full' })}
            </>
          ) : null}
        </Text>
      </PageLayout>

      {/*-- dialogs --*/}

      {/*-- delete dialog --*/}
      <DeleteDialog
        onClose={() => setDeleteOpen(false)}
        onDelete={(callback) =>
          tryDeleteReportEdition(edition_id, () => {
            callback()
            history.push(`/reports/${id}/editions`)
          })
        }
        open={deleteOpen}
        title={edition.title}
        type={t('global', 'edition', true)}
        unlockPhrase={t('global', 'delete')}
      />
    </>
  )
}

export default ReportEdition
